/**
 * useDesktopDeepLink — subscribe to deep-link events dispatched by the
 * Electron shell (`window.ainovisDesktop.onDeepLink`).
 *
 * When a deep-link arrives we map it to an internal React Router navigation.
 * The payload shape is validated by the Electron main process (see
 * `desktop/src/deepLink.ts`); here we only translate to a URL path.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface DeepLinkPayload {
  readonly route: string;
  readonly query: Record<string, string>;
}

interface DesktopBridge {
  readonly onDeepLink?: (cb: (payload: DeepLinkPayload) => void) => () => void;
}

const ROUTE_TO_PATH: Record<string, string> = {
  // Canonical Phase-10 route.
  launch: '/launch',
  report: '/reports/v2/edit/draft',
  login: '/login',
};

export function useDesktopDeepLink(): void {
  const navigate = useNavigate();

  useEffect(() => {
    const bridge = (window as unknown as { ainovisDesktop?: DesktopBridge })
      .ainovisDesktop;
    if (!bridge?.onDeepLink) return;

    const unsubscribe = bridge.onDeepLink((payload) => {
      const basePath = ROUTE_TO_PATH[payload.route];
      if (!basePath) return;

      const qs = new URLSearchParams(payload.query).toString();
      const target = qs ? `${basePath}?${qs}` : basePath;
      navigate(target, { replace: false });
    });

    return unsubscribe;
  }, [navigate]);
}
